import React, { useLayoutEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { baseUrl, servicesSubbrokerClientLookupURL, servicesSubbrokerCodeAvailabilityURL, servicesSubbrokerSignupOtpVerifyURL, servicesSubbrokerSignupSubmitURL } from '../utils/Urls'
import './SubbrokerSignup.css'

const initialForm = {
  atsClientCode: '',
  fullName: '', mobile: '', email: '', city: '', state: '', pan: '',
  subbrokerCode: '', referralCode: '', consent: false,
  companyBrokerageShare: 50, brokerBrokerageShare: 50,
  bankAccountHolder: '', bankName: '', bankAccountNumber: '', bankIfsc: '',
  panDocument: '', identityDocument: '', bankDocument: '',
}

const documentViewUrl = (value) => {
  const path = String(value || '').trim()
  if (!path) return ''
  if (/^https?:\/\//i.test(path)) return path
  const mediaPath = path.replace(/^\/+/, '').replace(/^media\//i, '')
  try { return new URL(mediaPath, 'https://kyc.adityatrading.in/media/').href } catch (_) { return '' }
}

export default function SubbrokerSignup() {
  const [form, setForm] = useState(initialForm)
  const [submitted, setSubmitted] = useState(false)
  const [step, setStep] = useState(1)
  const [submitting, setSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState('')
  const [subbrokerCodeError, setSubbrokerCodeError] = useState('')
  const [codeAvailabilityStatus, setCodeAvailabilityStatus] = useState('idle')
  const [codeAvailabilityMessage, setCodeAvailabilityMessage] = useState('')
  const [ticketId, setTicketId] = useState('')
  const [onboardingPath, setOnboardingPath] = useState('')
  const [clientLookupStatus, setClientLookupStatus] = useState('idle')
  const [clientLookupMessage, setClientLookupMessage] = useState('')
  const [clientDataSource, setClientDataSource] = useState('')
  const [otpChallengeId, setOtpChallengeId] = useState('')
  const [mobileOtp, setMobileOtp] = useState('')
  const [otpStatus, setOtpStatus] = useState('idle')
  const [otpMessage, setOtpMessage] = useState('')
  const [maskedMobile, setMaskedMobile] = useState('')

  useLayoutEffect(() => {
    const root = document.getElementById('root')
    const page = document.documentElement
    if (root) root.classList.add('signup-route-root')
    page.classList.add('signup-document-root')
    window.scrollTo(0, 0)
    const params = new URLSearchParams(window.location.search)
    const referralCode = (params.get('referral') || params.get('ref') || '').trim().toUpperCase()
    if (referralCode) {
      setForm((current) => ({ ...current, referralCode: current.referralCode || referralCode }))
    }
    return () => {
      if (root) root.classList.remove('signup-route-root')
      page.classList.remove('signup-document-root')
    }
  }, [])

  const update = ({ target }) => {
    const { name, type, checked, value, files } = target
    const nextValue = type === 'checkbox'
      ? checked
      : type === 'file'
        ? (files?.[0]?.name || '')
        : name === 'subbrokerCode' || name === 'atsClientCode'
          ? value.toUpperCase().replace(/[^A-Z0-9]/g, '')
          : value
    if (name === 'atsClientCode') {
      setForm((current) => ({
        ...current, atsClientCode: nextValue,
        fullName: '', mobile: '', email: '', city: '', state: '', pan: '',
        bankAccountHolder: '', bankName: '', bankAccountNumber: '', bankIfsc: '',
        panDocument: '', identityDocument: '', bankDocument: '',
      }))
      setClientLookupStatus('idle')
      setClientLookupMessage('')
      setClientDataSource('')
      setOtpChallengeId('')
      setOtpStatus('idle')
      setOtpMessage('')
      setMobileOtp('')
      setMaskedMobile('')
    } else {
      setForm((current) => ({ ...current, [name]: nextValue }))
    }
    if (name === 'subbrokerCode' && /^[A-Z][A-Z0-9]*00$/.test(nextValue)) {
      setSubbrokerCodeError('')
    }
    if (name === 'subbrokerCode') {
      setCodeAvailabilityStatus('idle')
      setCodeAvailabilityMessage('')
    }
  }

  // Step 1 no longer reveals any KYC PII (name/mobile/email/PAN/bank/documents)
  // from just an ATS client code -- lookupClient() only validates the code and
  // sends an ownership-proving OTP to the *registered* mobile/email/WhatsApp,
  // showing just a masked mobile preview. verifyOtp() is what actually reveals
  // the account's details, once the OTP proves the caller owns it.
  const lookupClient = async () => {
    const clientCode = form.atsClientCode.trim().toUpperCase()
    if (!clientCode) {
      setClientLookupStatus('error')
      setClientLookupMessage('Enter your ATS client code first.')
      return
    }
    setClientLookupStatus('loading')
    setClientLookupMessage('Checking ATS KYC records…')
    setOtpChallengeId('')
    setOtpStatus('idle')
    setOtpMessage('')
    setMobileOtp('')
    setMaskedMobile('')
    try {
      const response = await axios.post(servicesSubbrokerClientLookupURL, { client_code: clientCode })
      const data = response?.data
      if (!data?.challenge_id) throw new Error('Verification could not be started')
      setForm((current) => ({ ...current, atsClientCode: data.client_code || clientCode }))
      setOtpChallengeId(data.challenge_id)
      setMaskedMobile(data.masked_mobile || '')
      setMobileOtp('')
      setOtpStatus('sent')
      setOtpMessage(data.message || `An OTP was sent to your registered mobile (${data.masked_mobile}).`)
      setClientLookupStatus('success')
      setClientLookupMessage('')
    } catch (error) {
      setClientLookupStatus('error')
      setClientLookupMessage(error?.response?.data?.detail || error?.message || 'Unable to fetch this client code.')
    }
  }

  const verifyOtp = async () => {
    setOtpStatus('verifying')
    try {
      const response = await axios.post(servicesSubbrokerSignupOtpVerifyURL, {
        challenge_id: otpChallengeId, otp: mobileOtp,
      })
      const client = response?.data?.client
      if (client) {
        setForm((current) => ({
          ...current,
          fullName: client.full_name || '',
          mobile: client.mobile || '',
          email: client.email || '',
          city: client.city || '',
          state: client.state || '',
          pan: client.pan || '',
          bankAccountHolder: client.bank_account_holder || client.full_name || '',
          bankName: client.bank_name || '',
          bankAccountNumber: client.bank_account_number || '',
          bankIfsc: client.bank_ifsc || '',
          panDocument: client.documents?.pan || '',
          identityDocument: client.documents?.identity || '',
          bankDocument: client.documents?.bank || '',
        }))
        setClientDataSource(client.source || 'KYC')
      }
      setOtpStatus('verified')
      setOtpMessage(response.data.message)
    } catch (error) {
      setOtpStatus('error')
      setOtpMessage(error?.response?.data?.detail || 'OTP verification failed.')
    }
  }

  const validateSubbrokerCode = () => {
    const code = form.subbrokerCode.trim().toUpperCase()
    if (!code) {
      setSubbrokerCodeError('Create your Subbroker code to continue.')
      return false
    }
    if (!/^[A-Z][A-Z0-9]*00$/.test(code)) {
      setSubbrokerCodeError('Start with a letter, use only letters and numbers, and end with 00 or 000 (example: TEST00 or TEST000).')
      return false
    }
    setSubbrokerCodeError('')
    setForm((current) => ({ ...current, subbrokerCode: code }))
    return true
  }

  const checkSubbrokerCodeAvailability = async () => {
    if (!validateSubbrokerCode()) {
      setCodeAvailabilityStatus('error')
      setCodeAvailabilityMessage('Enter a valid code ending in 00 or 000 before checking availability.')
      return false
    }
    const code = form.subbrokerCode.trim().toUpperCase()
    setCodeAvailabilityStatus('checking')
    setCodeAvailabilityMessage('Checking code availability…')
    try {
      const response = await axios.post(servicesSubbrokerCodeAvailabilityURL, { subbroker_code: code })
      const available = response?.data?.available === true
      setCodeAvailabilityStatus(available ? 'available' : 'taken')
      setCodeAvailabilityMessage(available ? `${code} is available.` : `${code} is already taken. Choose another code.`)
      return available
    } catch (error) {
      setCodeAvailabilityStatus('error')
      setCodeAvailabilityMessage(error?.response?.data?.detail || 'Unable to check code availability. Please try again.')
      return false
    }
  }

  const continueToNextStep = async () => {
    if (step === 1 && otpStatus !== 'verified') {
      setClientLookupStatus('error')
      setClientLookupMessage(otpChallengeId
        ? 'Verify the OTP sent to your registered mobile before continuing.'
        : 'Fetch and verify your ATS client code before continuing.')
      return
    }
    if (!validateSubbrokerCode()) return
    if (codeAvailabilityStatus !== 'available') {
      const available = await checkSubbrokerCodeAvailability()
      if (!available) return
    }
    setStep((current) => current + 1)
  }

  const submit = async (event) => {
    event.preventDefault()
    if (!validateSubbrokerCode()) {
      setStep(1)
      return
    }
    if (codeAvailabilityStatus !== 'available') {
      const available = await checkSubbrokerCodeAvailability()
      if (!available) {
        setStep(1)
        return
      }
    }
    if (otpStatus !== 'verified' || !otpChallengeId) {
      setSubmissionError('Verify the OTP before submitting.')
      return
    }
    setSubmitting(true)
    setSubmissionError('')
    const mobile = form.mobile.replace(/\D/g, '')
    const pan = form.pan.trim().toUpperCase()
    const application = {
      ...form, mobile, pan, subbrokerCode: form.subbrokerCode.trim().toUpperCase(),
      submittedAt: new Date().toISOString(), status: 'pending',
    }

    try {
      const response = await axios.post(servicesSubbrokerSignupSubmitURL, {
        challenge_id: otpChallengeId,
        ats_client_code: application.atsClientCode, subbroker_code: application.subbrokerCode,
        full_name: application.fullName, mobile: application.mobile, email: application.email,
        city: application.city, state: application.state, pan: application.pan,
        referral_code: application.referralCode,
        company_brokerage_share: application.companyBrokerageShare,
        broker_brokerage_share: application.brokerBrokerageShare,
        bank_account_holder: application.bankAccountHolder, bank_name: application.bankName,
        bank_account_number: application.bankAccountNumber, bank_ifsc: application.bankIfsc,
        pan_document: application.panDocument, identity_document: application.identityDocument,
        bank_document: application.bankDocument, consent: application.consent,
      })
      if (response?.data?.status !== 'success' || !response?.data?.ticket_id) throw new Error('CRM did not return a ticket number')
      const previous = JSON.parse(localStorage.getItem('subbroker_signup_applications') || '[]')
      const savedApplication = { ...application, ticketId: response.data.ticket_id, applicationId: response.data.application_number, referenceNumber: response.data.reference_number }
      localStorage.setItem('subbroker_signup_applications', JSON.stringify([...previous, savedApplication]))
      setTicketId(String(response.data.ticket_id))
      setForm(savedApplication)
      setSubmitted(true)
    } catch (error) {
      setSubmissionError(error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Could not submit the application to Support. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const documentDetailsComplete = Boolean(
    form.pan.trim() && form.bankAccountHolder.trim() && form.bankName.trim() &&
    form.bankAccountNumber.trim() && form.bankIfsc.trim()
  )

  return <main className="signup-page">
    <aside className="signup-aside">
      <div className="signup-aside-inner">
        <span className="signup-kicker">ATS PARTNER NETWORK</span>
        <h1>Build your business with one clear platform.</h1>
        <p>Submit your Subbroker application. Our onboarding team will verify your information before activating portal access.</p>
        <div className="signup-steps">
          <div className={step === 1 ? 'active' : step > 1 ? 'complete' : ''}><b>{step > 1 ? '✓' : '01'}</b><span><strong>Verify your contact details</strong>Complete mobile and email verification.</span></div>
          <div className={step === 2 ? 'active' : step > 2 ? 'complete' : ''}><b>{step > 2 ? '✓' : '02'}</b><span><strong>Verify required documents</strong>Review your PAN, identity and bank documents.</span></div>
          <div className={step === 3 ? 'active' : ''}><b>03</b><span><strong>Submit for review</strong>Review your details and submit the application.</span></div>
        </div>
      </div>
      <small>Secure partner onboarding</small>
    </aside>

    <section className="signup-content">
      <header className="signup-header">
        <img src={`${process.env.PUBLIC_URL || ''}/ats-logo.png`} alt="Aditya Trading Solutions" />
        <Link to="/login">Already registered? <b>Sign in</b></Link>
      </header>

      {!onboardingPath ? <div className="signup-form-wrap signup-path-wrap">
        <div className="signup-title">
          <span>CHOOSE YOUR ONBOARDING PATH</span>
          <h2>Do you already have an ATS client code?</h2>
          <p>Every Sub-Broker applicant must first have an ATS demat account and a verified client code.</p>
        </div>
        <div className="signup-path-grid">
          <button type="button" className="signup-path-card" onClick={() => setOnboardingPath('existing')}>
            <b>Existing ATS client</b>
            <strong>I have an ATS client code</strong>
            <span>Continue with your client code and verify the registered contact details.</span>
            <em>Continue with client code →</em>
          </button>
          <a className="signup-path-card" href={baseUrl}>
            <b>New ATS client</b>
            <strong>I don’t have an ATS client code</strong>
            <span>Create your ATS demat account and complete KYC before applying as a Sub-Broker.</span>
            <em>Create demat account →</em>
          </a>
        </div>
        <p className="signup-path-note">After KYC approval, return here with the ATS client code issued to you.</p>
      </div> : submitted ? <div className="signup-success" role="status">
        <div className="signup-success-icon">✓</div>
        <span>APPLICATION SUBMITTED</span>
        <h2>Thank you, {form.fullName.split(' ')[0]}.</h2>
        <p>Your application number and Support ticket number is <strong>#{ticketId}</strong>. Reference: <strong>{form.referenceNumber}</strong>. Your selected Subbroker code <strong>{form.subbrokerCode}</strong> will be activated after the onboarding team verifies your application.</p>
        <Link className="signup-primary-link" to="/login">Return to sign in <span>→</span></Link>
      </div> : <div className="signup-form-wrap">
        <button className="signup-back signup-change-path" type="button" onClick={() => setOnboardingPath('')}>← Change onboarding path</button>
        <div className="signup-title"><span>STEP {step} OF 3</span><h2>{step === 1 ? 'Verify your ATS client details' : step === 2 ? 'Upload your documents' : 'Review and submit'}</h2><p>{step === 1 ? 'Continue using the client code issued after ATS demat KYC.' : step === 2 ? 'Select the verification files for your application.' : 'Check your information before submitting it for review.'}</p></div>
        <form className="signup-form" onSubmit={submit} noValidate>
          {step === 1 && <div className="signup-grid">
            <div className="signup-wide signup-client-lookup">
              <label>ATS client code *<input name="atsClientCode" value={form.atsClientCode} onChange={update} autoCapitalize="characters" placeholder="Example: CL018" disabled={otpStatus === 'verified'} /></label>
              <button type="button" onClick={lookupClient} disabled={clientLookupStatus === 'loading' || otpStatus === 'verified'}>{clientLookupStatus === 'loading' ? 'Checking…' : otpStatus === 'verified' ? 'Verified ✓' : 'Fetch details'}</button>
              {clientLookupMessage && <p className={`signup-lookup-${clientLookupStatus}`} role={clientLookupStatus === 'error' ? 'alert' : 'status'}>{clientLookupMessage}</p>}
            </div>
            {otpChallengeId && <div className="signup-wide signup-otp-block">
              <div><span>OWNERSHIP VERIFICATION</span></div>
              {otpStatus === 'verified'
                ? <p className="signup-field-success">✓ Verified via the OTP sent to your registered mobile ({maskedMobile}).</p>
                : <>
                    <p>{otpMessage || `An OTP was sent to your registered mobile (${maskedMobile}), email and WhatsApp. This confirms you own this ATS account before we show its details.`}</p>
                    <div className="signup-otp-grid signup-otp-sequential">
                      <label>Enter OTP<input value={mobileOtp} onChange={(e) => setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="6-digit OTP" /></label>
                      <button className="signup-otp-button" type="button" onClick={verifyOtp} disabled={mobileOtp.length !== 6 || otpStatus === 'verifying'}>{otpStatus === 'verifying' ? 'Verifying OTP...' : 'Verify OTP'}</button>
                      <button className="signup-otp-resend" type="button" onClick={lookupClient} disabled={clientLookupStatus === 'loading'}>Resend OTP</button>
                    </div>
                    {otpStatus === 'error' && otpMessage && <p className="signup-field-error" role="alert">{otpMessage}</p>}
                  </>}
            </div>}
            {otpStatus === 'verified' && <>
              <label className="signup-wide">Full name *<input name="fullName" value={form.fullName} onChange={update} readOnly={clientDataSource === 'KYC'} autoComplete="name" placeholder="Enter full name" /></label>
              <label>Mobile number *<div className="signup-phone"><span>+91</span><input name="mobile" value={form.mobile} onChange={update} readOnly={clientDataSource === 'KYC'} autoComplete="tel" placeholder="Enter mobile number" /></div></label>
              <label>Email address *<input name="email" type="email" value={form.email} onChange={update} readOnly={clientDataSource === 'KYC'} autoComplete="email" placeholder="Enter email address" /></label>
              <label>City *<input name="city" value={form.city} onChange={update} readOnly={clientDataSource === 'KYC'} autoComplete="address-level2" placeholder="Enter city" /></label>
              <label>State *<input name="state" value={form.state} onChange={update} readOnly={clientDataSource === 'KYC'} autoComplete="address-level1" placeholder="Enter state" /></label>
              <label>PAN *<input name="pan" value={form.pan} onChange={update} readOnly={clientDataSource === 'KYC'} autoCapitalize="characters" placeholder="Enter PAN" /></label>
              <label>Referral code (optional)<input name="referralCode" value={form.referralCode} onChange={update} placeholder="Partner or employee code" /></label>
              <div className="signup-wide signup-code-builder">
                <div><span>YOUR LOGIN ID</span><strong>Choose your Subbroker code</strong><small>You create this code yourself. It cannot be left blank.</small></div>
                <label>Subbroker code *<input name="subbrokerCode" value={form.subbrokerCode} onChange={update} onBlur={() => { if (validateSubbrokerCode()) checkSubbrokerCodeAvailability() }} autoCapitalize="characters" placeholder="TEST00" pattern="[A-Za-z][A-Za-z0-9]*00" title="Subbroker code must start with a letter and end with 00 or 000" required aria-invalid={Boolean(subbrokerCodeError) || codeAvailabilityStatus === 'taken'} aria-describedby="subbroker-code-help" /></label>
                <p id="subbroker-code-help" className={subbrokerCodeError || ['taken', 'error'].includes(codeAvailabilityStatus) ? 'signup-field-error' : codeAvailabilityStatus === 'available' ? 'signup-field-success' : 'signup-field-help'}>{subbrokerCodeError || codeAvailabilityMessage || <>Use letters and numbers, ending in <b>00</b> or <b>000</b>. Example: <b>TEST00</b> or <b>TEST000</b></>}</p>
              </div>
              <div className="signup-wide signup-brokerage-share" aria-label="Fixed brokerage sharing">
                <span>BROKERAGE SHARING</span>
                <div><strong>Company <b>{form.companyBrokerageShare}%</b></strong><i>+</i><strong>Broker <b>{form.brokerBrokerageShare}%</b></strong></div>
                <small>This allocation is fixed and cannot be modified.</small>
              </div>
            </>}
          </div>}
          {step === 2 && <section className="signup-documents signup-documents-step">
            <div className="signup-section-heading">
              <span>IDENTIFICATION &amp; BANK DETAILS</span>
              <h3>Enter bank details before uploading documents</h3>
              <p>Your PAN from Step 1 will be used. Complete the bank information below to enable document uploads.</p>
            </div>
            <div className="signup-grid signup-document-details">
              <label>Account holder name *<input name="bankAccountHolder" value={form.bankAccountHolder} onChange={update} readOnly={clientDataSource === 'KYC'} placeholder="Enter account holder name" /></label>
              <label>Bank name *<input name="bankName" value={form.bankName} onChange={update} readOnly={clientDataSource === 'KYC'} placeholder="Enter bank name" /></label>
              <label>Account number *<input name="bankAccountNumber" value={form.bankAccountNumber} onChange={update} readOnly={clientDataSource === 'KYC'} inputMode="numeric" placeholder="Enter bank account number" /></label>
              <label>IFSC code *<input name="bankIfsc" value={form.bankIfsc} onChange={update} readOnly={clientDataSource === 'KYC'} autoCapitalize="characters" placeholder="Enter IFSC code" /></label>
            </div>
            <div className="signup-section-heading signup-upload-heading">
              <span>DOCUMENT UPLOADS</span>
              <h3>PAN, identity proof and bank proof</h3>
              <p>{documentDetailsComplete ? 'PDF, JPG and PNG files can now be selected.' : 'Complete all PAN and bank fields above to unlock uploads.'}</p>
            </div>
            <div className="signup-document-grid">
              {[
                ['panDocument', 'PAN card', 'Tax identity document'],
                ['identityDocument', 'Identity proof', 'Aadhaar, passport or voter ID'],
                ['bankDocument', 'Bank proof', 'Cancelled cheque or bank statement'],
              ].map(([name, title, description]) => <label className={`signup-document ${!documentDetailsComplete ? 'locked' : ''} ${form[name] ? 'selected' : ''}`} key={name}>
                <input type="file" name={name} accept=".pdf,.jpg,.jpeg,.png" onChange={update} disabled={!documentDetailsComplete} />
                <span className="signup-document-icon">{form[name] ? '✓' : '↑'}</span>
                <span className="signup-document-copy"><strong>{title}</strong><small>{form[name] || description}</small>{form[name] && <em>Selected successfully</em>}</span>
                <span className="signup-document-actions">
                  {clientDataSource === 'KYC' && documentViewUrl(form[name]) && <a href={documentViewUrl(form[name])} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>View</a>}
                  <span className="signup-document-action">{!documentDetailsComplete ? 'Locked' : form[name] ? 'Replace' : 'Choose file'}</span>
                </span>
              </label>)}
            </div>
          </section>}
          {step === 3 && <section className="signup-review">
            <div className="signup-review-block"><div><span>SELECTED SUBBROKER CODE</span><button type="button" onClick={() => setStep(1)}>Edit</button></div><p><strong>{form.subbrokerCode}</strong> — created by the applicant</p></div>
            <div className="signup-review-block"><div><span>APPLICANT DETAILS</span><button type="button" onClick={() => setStep(1)}>Edit</button></div><dl><div><dt>Full name</dt><dd>{form.fullName || 'Not provided'}</dd></div><div><dt>Mobile</dt><dd>{form.mobile || 'Not provided'}</dd></div><div><dt>Email</dt><dd>{form.email || 'Not provided'}</dd></div><div><dt>PAN</dt><dd>{form.pan || 'Not provided'}</dd></div><div><dt>Referral code</dt><dd>{form.referralCode || 'Not provided'}</dd></div><div><dt>Location</dt><dd>{[form.city, form.state].filter(Boolean).join(', ') || 'Not provided'}</dd></div><div><dt>Brokerage sharing</dt><dd>Company {form.companyBrokerageShare}% · Broker {form.brokerBrokerageShare}%</dd></div></dl></div>
            <div className="signup-review-block"><div><span>BANK DETAILS</span><button type="button" onClick={() => setStep(2)}>Edit</button></div><dl><div><dt>Account holder</dt><dd>{form.bankAccountHolder || 'Not provided'}</dd></div><div><dt>Bank</dt><dd>{form.bankName || 'Not provided'}</dd></div><div><dt>Account number</dt><dd>{form.bankAccountNumber || 'Not provided'}</dd></div><div><dt>IFSC</dt><dd>{form.bankIfsc || 'Not provided'}</dd></div></dl></div>
            <div className="signup-review-block"><div><span>DOCUMENTS</span><button type="button" onClick={() => setStep(2)}>Edit</button></div><p>{[form.panDocument, form.identityDocument, form.bankDocument].filter(Boolean).length} of 3 documents selected</p></div>
            <div className="signup-review-block signup-otp-block">
              <div><span>IDENTITY VERIFICATION</span></div>
              <p className="signup-field-success">✓ Verified via the OTP sent to your registered mobile ({maskedMobile}), email and WhatsApp.</p>
            </div>
            <label className="signup-consent"><input type="checkbox" name="consent" checked={form.consent} onChange={update} /><span>I confirm that the details provided are correct and agree to be contacted for Subbroker verification and onboarding.</span></label>
          </section>}
          <div className="signup-navigation">
            {step > 1 && <button className="signup-back" type="button" onClick={() => setStep((current) => current - 1)}>← Back</button>}
            {step < 3 ? <button className="signup-submit" type="button" onClick={continueToNextStep}><span>Continue</span><span>→</span></button> : <button className="signup-submit" type="submit" disabled={submitting}><span>{submitting ? 'Creating Support ticket…' : 'Submit application'}</span><span>→</span></button>}
          </div>
          {submissionError && <p className="signup-error" role="alert">{submissionError}</p>}
          <p className="signup-note">Other field validation is disabled for testing. A valid self-created Subbroker code is mandatory.</p>
        </form>
      </div>}
    </section>
  </main>
}
